import { useCallback, useEffect, useReducer } from "react";
import { widthAuthHOC } from "../../hoc";
import { Heading2 } from "../../components/Headings";
import {
  Form,
  Fieldset,
  TextInput,
  MobileInput,
  OTPInput,
  Checkbox,
  Button,
  MessageBox,
  Dropdown,
} from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import {
  StyledAuthLinks,
  StyledLink,
  StyledButtonAsLink,
  StyledAuthFooterLinkText,
  StyledPara,
  StyledTextHighlight,
} from "../../components/styled";
import API from "../../api";
import { setAuthData } from "../../utils";
import { OTP_VERIFICATION_TYPES, RESPONSE_STATUSES } from "../../constants";
import { useQuery } from "@tanstack/react-query";
import { Grid, GridItem } from "../../components/Grid";

const initialState = {
  isLoading: false,
  responseStatus: "",
  responseMessage: "",
  routeStage: "Register",
  firmName: "",
  email: "",
  fullName: "",
  mobileNumber: "",
  password: "",

  firmType: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  gender: "",
  dateOfBirth: "",

  requestToken: "",
  oneTimePassword: "",
  isCheckedTnC: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "SET_STAGE":
      return { ...state, routeStage: action.stage };
    case "SET_RESPONSE":
      return {
        ...state,
        responseStatus: action.status,
        responseMessage: action.message,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.isLoading };
    default:
      return state;
  }
};

const Register = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: readMeta,
  });

  const onChange = (field, value) => {
    dispatch({ type: "SET_FIELD", field, value });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    dispatch({ type: "SET_LOADING", isLoading: true });

    const requestData =
      state.routeStage === "Register"
        ? {
            firmName: state.firmName,
            email: state.email,
            firmType: state.firmType,
            fullName: state.fullName,
            mobileNumber: state.mobileNumber,
            addressLine1: state.addressLine1,
            addressLine2: state.addressLine2,
            city: state.city,
            state: state.state,
            pincode: state.pincode,
            gender: state.gender,
            dateOfBirth: state.dateOfBirth,
            password: state.password,
            isCheckedTnC: state.isCheckedTnC,
          }
        : {
            requestType: OTP_VERIFICATION_TYPES.REGISTER,
            requestToken: state.requestToken,
            mobileNumber: state.mobileNumber,
            oneTimePassword: state.oneTimePassword,
          };

    API.post(
      state.routeStage === "Register"
        ? "/authentication/register"
        : "/authentication/verify-otp",
      requestData
    )
      .then((response) => {
        const { status, message, token, data } = response.data;
        if (status === RESPONSE_STATUSES.SUCCESS) {
          dispatch({
            type: "SET_RESPONSE",
            status: RESPONSE_STATUSES.SUCCESS,
            message,
          });
          if (state.routeStage === "Register") {
            dispatch({ type: "SET_STAGE", stage: "OTP" });
            dispatch({
              type: "SET_FIELD",
              field: "requestToken",
              value: token,
            });
          } else {
            setAuthData(token, data);
          }
        } else {
          dispatch({
            type: "SET_RESPONSE",
            status: RESPONSE_STATUSES.FAILED,
            message,
          });
          dispatch({
            type: "SET_FIELD",
            field: "requestToken",
            value: token,
          });
        }
      })
      .catch((error) => {
        dispatch({
          type: "SET_RESPONSE",
          status: RESPONSE_STATUSES.FAILED,
          message: error.message,
        });
      })
      .finally(() => {
        dispatch({ type: "SET_LOADING", isLoading: false });
      });
  };

  const getLocationDetails = useCallback(
    (e) => {
      e?.preventDefault();
      if (!state.pincode) return;
      dispatch({ type: "SET_LOADING", isLoading: true });
      API.get("/meta/pincode?pincode=" + state.pincode)
        .then((response) => {
          const { status, data } = response.data;
          if (status === RESPONSE_STATUSES.SUCCESS) {
            dispatch({
              type: "SET_FIELD",
              field: "state",
              value: data.state,
            });
            dispatch({
              type: "SET_FIELD",
              field: "city",
              value: data.city,
            });
          }
        })
        .catch((error) => console.error(error))
        .finally(() => {
          dispatch({ type: "SET_LOADING", isLoading: false });
        });
    },
    [state.pincode]
  );

  const resendOTP = (e) => {
    e.preventDefault();
    dispatch({ type: "SET_LOADING", isLoading: true });
    API.post("/authentication/resend-otp", {
      requestToken: state.requestToken,
      mobileNumber: state.mobileNumber,
    })
      .then((response) => {
        const { status, message, token } = response.data;
        dispatch({ type: "SET_RESPONSE", status, message });
        dispatch({ type: "SET_FIELD", field: "requestToken", value: token });
      })
      .catch((error) => {
        dispatch({
          type: "SET_RESPONSE",
          status: RESPONSE_STATUSES.FAILED,
          message: error.message,
        });
      })
      .finally(() => {
        dispatch({ type: "SET_LOADING", isLoading: false });
      });
  };

  useEffect(() => {
    if (state.pincode.length === 6) {
      getLocationDetails();
    }
  }, [getLocationDetails, state.pincode]);

  const firmTypeOptions = [{ name: "-- SELECT FIRM TYPE --", value: "" }];

  if (meta && meta?.firmTypes && meta?.firmTypes?.length > 0) {
    meta?.firmTypes?.forEach(({ id, type }) => {
      firmTypeOptions.push({
        name: type,
        value: id,
      });
    });
  }

  const renderStageFields = () => {
    if (state.routeStage === "Register") {
      return (
        <>
          <Fieldset>
            <TextInput
              value={state.firmName}
              onChange={(value) => onChange("firmName", value)}
              placeholder="Firm Name"
              disabled={state.isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Dropdown
              value={state.firmType}
              onChange={(value) => onChange("firmType", value)}
              placeholder="Firm Type"
              disabled={state.isLoading}
              options={firmTypeOptions}
            />
          </Fieldset>
          <Grid>
            <GridItem>
          <Fieldset>
            <TextInput
              value={state.fullName}
              onChange={(value) => onChange("fullName", value)}
              placeholder="Full Name"
              disabled={state.isLoading}
            />
          </Fieldset>
          </GridItem>
          <GridItem>
          <Fieldset>
            <MobileInput
              value={state.mobileNumber}
              onChange={(value) => onChange("mobileNumber", value)}
              placeholder="Mobile Number"
              disabled={state.isLoading}
            />
          </Fieldset>
          </GridItem>
          </Grid>
          <Fieldset>
            <TextInput
              value={state.email}
              onChange={(value) => onChange("email", value)}
              placeholder="email"
              maxLength={255}
              disabled={state.isLoading}
            />
          </Fieldset>
          {/* <Fieldset>
            <Dropdown
              value={state.gender}
              onChange={(value) => onChange("gender", value)}
              placeholder="Gender"
              disabled={state.isLoading}
              options={[
                {
                  name: "-- SELECT GENDER --",
                  value: "",
                },
                {
                  name: "Male",
                  value: "Male",
                },
                {
                  name: "Female",
                  value: "Female",
                },
                {
                  name: "Others",
                  value: "Others",
                },
              ]}
            />
          </Fieldset>
          <Fieldset>
            <TextInput
              type="date"
              value={state.dateOfBirth}
              onChange={(value) => onChange("dateOfBirth", value)}
              placeholder="Date Of Birth"
              disabled={state.isLoading}
            />
          </Fieldset>
          <Fieldset>
            <PasswordInput
              value={state.password}
              onChange={(value) => onChange("password", value)}
              placeholder="Password"
              disabled={state.isLoading}
            />
          </Fieldset> */}
<Grid>
            <GridItem>
          <Fieldset>
            <TextInput
              value={state.addressLine1}
              onChange={(value) => onChange("addressLine1", value)}
              placeholder="Address Line 1"
              maxLength={1000}
              disabled={state.isLoading}
            />
          </Fieldset>
          </GridItem>
          <GridItem>
          <Fieldset>
            <TextInput
              value={state.addressLine2}
              onChange={(value) => onChange("addressLine2", value)}
              placeholder="Address Line 2"
              maxLength={1000}
              disabled={state.isLoading}
            />
          </Fieldset>
          </GridItem>
          </Grid>
          <Fieldset>
            <TextInput
              value={state.pincode}
              onChange={(value) => onChange("pincode", value)}
              placeholder="Pincode"
              maxLength={8}
              disabled={state.isLoading}
            />
          </Fieldset>
          <Grid>
            <GridItem>
              <Fieldset>
                <TextInput
                  value={state.city}
                  onChange={(value) => onChange("city", value)}
                  placeholder="City"
                  maxLength={1000}
                  disabled={state.isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <TextInput
                  value={state.state}
                  onChange={(value) => onChange("state", value)}
                  placeholder="State"
                  maxLength={1000}
                  disabled={state.isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>

          <Fieldset>
            <Checkbox
              value={state.isCheckedTnC}
              onChange={(value) => onChange("isCheckedTnC", value)}
            >
              By creating an account, you agree to our{" "}
              <StyledLink to="/privacy-policy" target="_blank">
                Privacy Policy
              </StyledLink>
              ,{" "}
              <StyledLink to="/terms-and-conditions" target="_blank">
                Terms & Conditions
              </StyledLink>
              , and{" "}
              <StyledLink to="/refund-policy" target="_blank">
                Refund Policy
              </StyledLink>
              .
            </Checkbox>
          </Fieldset>
        </>
      );
    } else if (state.routeStage === "OTP") {
      return (
        <>
          <Fieldset>
            <StyledPara>
              Please enter your OTP sent to{" "}
              <StyledTextHighlight>{state.mobileNumber}</StyledTextHighlight>
            </StyledPara>
          </Fieldset>
          <Fieldset>
            <OTPInput
              value={state.oneTimePassword}
              onChange={(value) => onChange("oneTimePassword", value)}
              placeholder="OTP"
              disabled={state.isLoading}
            />
          </Fieldset>
        </>
      );
    }
    return null;
  };

  return (
    <>
      <Loader isLoading={state.isLoading} />
      <Heading2 textAlign="center">
        {state.routeStage === "Register"
          ? "Create your account"
          : "Verify & Proceed"}
      </Heading2>
      <Form action="#" method="POST" onSubmit={onSubmit}>
        {renderStageFields()}
        {state.routeStage === "OTP" && (
          <Fieldset>
            <StyledAuthLinks>
              <StyledButtonAsLink type="button" onClick={resendOTP}>
                Resend OTP
              </StyledButtonAsLink>
            </StyledAuthLinks>
          </Fieldset>
        )}
        {state.responseStatus && state.responseMessage && (
          <Fieldset>
            <MessageBox
              status={state.responseStatus}
              message={state.responseMessage}
            />
          </Fieldset>
        )}
        <Fieldset>
          <Button disabled={state.isLoading}>
            {state.routeStage === "Register" ? "Register" : "Verify & Proceed"}
          </Button>
        </Fieldset>
      </Form>
      {state.routeStage === "Register" && (
        <StyledAuthFooterLinkText>
          Already have an account?{" "}
          <StyledLink to="/login" title="Login">
            Login
          </StyledLink>
        </StyledAuthFooterLinkText>
      )}
    </>
  );
};

export default widthAuthHOC(Register);
